import Axios from 'axios';
import Cookies from 'js-cookie';
import { useMutation, useQuery } from 'react-query';

import apiUrls from 'base/api/urls';
import { StandardError } from 'base/api/errors';
import { useAuthStore } from 'common/hooks';
import { ACCESS_TOKEN, EMAIL_LOGIN, REFRESH_TOKEN } from './consts';
import { clearAuthState } from '../../base/api/utils';

type RoleResponse = {
  email: string;
  is_admin: boolean;
};

export const useRole = () => {
  const { valid, invalid } = useAuthStore(({ valid, invalid }) => ({ valid, invalid }));
  return useQuery<RoleResponse, StandardError>(apiUrls.AUTH.WHOAMI, {
    enabled: false,
    onSuccess: (data) => {
      if (data.is_admin) {
        valid();
      } else {
        invalid();
      }
    },
    onError: () => {
      clearAuthState();
    },
  });
};

type LoginResponse = {
  [ACCESS_TOKEN]: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  access_expires_at: string;
  refresh_expires_at: string;
  expires_at: number;
  sub: string;
};

type LoginVars = {
  email: string;
  password?: string;
  token?: string | string[];
  digit_code?: string;
};

export const useLogin = () => {
  const { refetch: getRole } = useRole();
  const { revalidate } = useAuthStore(({ revalidate }) => ({
    revalidate,
  }));
  return useMutation<LoginResponse, StandardError, LoginVars>(
    (data) => Axios.post(apiUrls.AUTH.LOGIN, data),
    {
      onSuccess: (data) => {
        Cookies.set(ACCESS_TOKEN, data[ACCESS_TOKEN], {
          expires: new Date(data.expires_at),
          sameSite: 'Strict',
          secure: true,
        });
        Cookies.set(REFRESH_TOKEN, data.refresh_token, {
          expires: new Date(data.refresh_expires_at),
          sameSite: 'Strict',
          secure: true,
        });
        Cookies.remove(EMAIL_LOGIN);

        revalidate();
        getRole();
      },
      onError: () => {
        clearAuthState();
      },
    },
  );
};

type LoginEmailVars = {
  email: string;
};

export const useSendLoginEmail = () => {
  return useMutation<null, StandardError, LoginEmailVars>(
    (data) => Axios.post(apiUrls.AUTH.LOGIN_TOKEN, data),
    {
      onSuccess: (_data, variables) => {
        const in30Minutes = 1 / 48;
        Cookies.set(EMAIL_LOGIN, variables.email, {
          expires: in30Minutes,
        });
      },
    },
  );
};

export const useLogout = () => {
  return useMutation<unknown, StandardError>(() => Axios.post(apiUrls.AUTH.LOGOUT), {
    onSettled: () => {
      clearAuthState();
    },
  });
};

export const useAzureLink = () => {
  return useQuery(apiUrls.AUTH.SSO_SESSION, { enabled: false });
};

export const useAzureLogin = () => {
  return useMutation((data) => Axios.post(apiUrls.AUTH.SSO_LOGIN, data));
};
